/**
 * Patient Intake Form Component
 * Comprehensive psychiatric intake form for patient assessment
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  LinearProgress,
  Divider,
  Stack,
  FormHelperText,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore,
  Psychology,
  Warning,
  CheckCircle,
  Assignment,
  Timeline,
  Mood,
  Bedtime,
  Restaurant,
  FitnessCenter,
  Security,
  FamilyRestroom,
  Work,
  School,
  LocalHospital,
  Medication
} from '@mui/icons-material';
import { toast } from 'react-toastify';

// Error handling
import { handleApiError, handleValidationErrors, getSuccessMessage } from '../../utils/errorHandler';

// API Service
import { intakeFormApi, formatIntakeFormData, parseIntakeFormData } from '../../services/api/patientComprehensive';

const PatientIntakeForm = ({ onSaved, onSkip, initialData }) => {
  const [formData, setFormData] = useState({
    // Chief Complaint & Present Illness
    chiefComplaint: '',
    durationOfSymptoms: '',
    presentIllness: '',
    symptomOnset: '',
    symptomSeverity: '',
    
    // Mood & Affect
    mood: '',
    affect: '',
    anxietyLevel: '',
    depressionLevel: '',
    
    // Sleep & Appetite
    sleepDuration: '',
    sleepQuality: '',
    sleepIssues: [],
    appetiteChanges: '',
    weightChanges: '',
    weightChangeAmount: '',
    
    // Cognitive Functioning
    energyLevel: '',
    activityLevel: '',
    concentration: '',
    memory: '',
    decisionMaking: '',
    
    // Risk Assessment
    suicidalThoughts: '',
    suicidalPlan: '',
    suicidalIntent: '',
    selfHarmThoughts: '',
    selfHarmBehavior: '',
    homicidalThoughts: '',
    homicidalPlan: '',
    homicidalIntent: '',
    
    // Substance Use
    currentSubstanceUse: {},
    substanceAbuseHistory: '',
    
    // Treatment History
    treatmentHistory: '',
    
    // Functioning
    socialFunctioning: '',
    occupationalFunctioning: '',
    relationshipStatus: '',
    
    // Trauma & Cultural
    traumaHistory: '',
    traumaDetails: '',
    ptsdSymptoms: [],
    culturalBackground: '',
    religiousBeliefs: '',
    culturalFactors: '',
    
    // Goals & Expectations
    treatmentGoals: [],
    patientExpectations: '',
    
    // Risk & Protective Factors
    riskFactors: [],
    protectiveFactors: [],
    
    // Clinical Assessment
    clinicalImpression: '',
    differentialDiagnosis: [],
    treatmentRecommendations: [],
    medicationRecommendations: [],
    therapyRecommendations: [],
    followUpPlan: '',
    urgencyLevel: '',
    isComplete: false
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [intakeExists, setIntakeExists] = useState(false);
  const [expandedSection, setExpandedSection] = useState('chief-complaint');

  // Sleep issues options
  const sleepIssuesOptions = [
    'Difficulty falling asleep',
    'Waking up frequently',
    'Early morning awakening',
    'Nightmares',
    'Sleep walking',
    'Restless legs',
    'Sleep apnea',
    'Insomnia',
    'Hypersomnia'
  ];

  // PTSD symptoms options
  const ptsdSymptomsOptions = [
    'Intrusive memories',
    'Flashbacks',
    'Nightmares',
    'Avoidance of reminders',
    'Negative mood changes',
    'Hypervigilance',
    'Startle response',
    'Sleep problems',
    'Concentration issues',
    'Irritability'
  ];

  // Treatment goals options
  const treatmentGoalsOptions = [
    'Reduce anxiety symptoms',
    'Improve mood',
    'Better sleep',
    'Reduce stress',
    'Improve relationships',
    'Better work performance',
    'Coping skills',
    'Trauma processing',
    'Substance use reduction',
    'Medication management'
  ];

  // Risk factors options
  const riskFactorsOptions = [
    'Previous suicide attempts',
    'Family history of suicide',
    'Substance abuse',
    'Access to lethal means',
    'Social isolation',
    'Recent loss',
    'Financial problems',
    'Legal issues',
    'Chronic pain',
    'Psychiatric diagnosis'
  ];

  // Protective factors options
  const protectiveFactorsOptions = [
    'Strong family support',
    'Religious/spiritual beliefs',
    'Access to mental health care',
    'Positive coping skills',
    'Future orientation',
    'Responsibility to others',
    'Pets',
    'Employment',
    'Stable housing',
    'Good physical health'
  ];

  // Load existing intake form on mount
  useEffect(() => {
    loadIntakeForm();
  }, []);

  const loadIntakeForm = async () => {
    try {
      setLoading(true);
      const response = await intakeFormApi.getIntakeForm();
      
      if (response.data && response.data.length > 0) {
        const intake = response.data[0];
        const parsedData = parseIntakeFormData(intake);
        setFormData(parsedData);
        setIntakeExists(true);
      }
    } catch (err) {
      console.error('Error loading intake form:', err);
      // Only show error if it's not a 404 (empty intake is expected for new patients)
      if (err.response?.status !== 404) {
        handleApiError(err, 'intake_load', toast);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleArrayChange = (field, value, action) => {
    setFormData(prev => ({
      ...prev,
      [field]: action === 'add' 
        ? [...(prev[field] || []), value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    // MINIMAL REQUIRED FIELDS - No required fields for gradual completion
    // All fields are optional to allow patients to fill gradually
    
    // Risk assessment validation (only if suicidal/homicidal thoughts are present)
    if (formData.suicidalThoughts === 'yes' && !formData.suicidalPlan) {
      newErrors.suicidalPlan = 'Please specify suicidal plan if suicidal thoughts are present';
    }
    
    if (formData.homicidalThoughts === 'yes' && !formData.homicidalPlan) {
      newErrors.homicidalPlan = 'Please specify homicidal plan if homicidal thoughts are present';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      handleValidationErrors(errors, toast);
      return;
    }

    try {
      setSaving(true);
      
      const formattedData = formatIntakeFormData(formData);
      
      if (intakeExists) {
        // Update existing intake form
        await intakeFormApi.updateIntakeForm(formData.id, formattedData);
        toast.success(getSuccessMessage('intake_updated'));
      } else {
        // Create new intake form
        await intakeFormApi.createIntakeForm(formattedData);
        setIntakeExists(true);
        toast.success(getSuccessMessage('intake_saved'));
      }
      
      // Redirect to profile page after successful save
      window.location.href = '/patient-dashboard/profile';
    } catch (err) {
      console.error('Error saving intake form:', err);
      handleApiError(err, 'intake_save', toast);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    // Redirect to profile page
    window.location.href = '/patient-dashboard/profile';
  };

  const handleSectionChange = (section) => (event, isExpanded) => {
    setExpandedSection(isExpanded ? section : false);
  };

  if (loading) {
    return (
      <Box p={3}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading intake form...
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3} maxWidth={1000} mx="auto">
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            <Psychology sx={{ mr: 1, verticalAlign: 'middle' }} />
            Psychiatric Intake Form
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This comprehensive assessment helps us understand your mental health needs and provide the best care possible.
          </Typography>

          <Stack spacing={2}>
            {/* Chief Complaint & Present Illness */}
            <Accordion 
              expanded={expandedSection === 'chief-complaint'} 
              onChange={handleSectionChange('chief-complaint')}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">
                  <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Chief Complaint & Present Illness
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Chief Complaint *"
                      multiline
                      rows={3}
                      value={formData.chiefComplaint}
                      onChange={(e) => handleInputChange('chiefComplaint', e.target.value)}
                      error={!!errors.chiefComplaint}
                      helperText={errors.chiefComplaint || "What brings you here today? Describe your main concern."}
                      placeholder="e.g., I've been feeling very anxious and depressed for the past 3 months..."
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Duration of Symptoms *"
                      value={formData.durationOfSymptoms}
                      onChange={(e) => handleInputChange('durationOfSymptoms', e.target.value)}
                      error={!!errors.durationOfSymptoms}
                      helperText={errors.durationOfSymptoms}
                      placeholder="e.g., 3 months, 1 year, since childhood..."
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Symptom Severity</InputLabel>
                      <Select
                        value={formData.symptomSeverity}
                        onChange={(e) => handleInputChange('symptomSeverity', e.target.value)}
                        label="Symptom Severity"
                      >
                        <MenuItem value="">Select Severity</MenuItem>
                        <MenuItem value="mild">Mild</MenuItem>
                        <MenuItem value="moderate">Moderate</MenuItem>
                        <MenuItem value="severe">Severe</MenuItem>
                        <MenuItem value="very_severe">Very Severe</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Present Illness"
                      multiline
                      rows={4}
                      value={formData.presentIllness}
                      onChange={(e) => handleInputChange('presentIllness', e.target.value)}
                      placeholder="Describe the course of your current symptoms, including onset, progression, and any triggers..."
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Mood & Affect */}
            <Accordion 
              expanded={expandedSection === 'mood-affect'} 
              onChange={handleSectionChange('mood-affect')}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">
                  <Mood sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Mood & Affect
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Current Mood"
                      value={formData.mood}
                      onChange={(e) => handleInputChange('mood', e.target.value)}
                      placeholder="e.g., depressed, anxious, irritable, euthymic..."
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Affect"
                      value={formData.affect}
                      onChange={(e) => handleInputChange('affect', e.target.value)}
                      placeholder="e.g., congruent, flat, labile, restricted..."
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Anxiety Level (1-10)</InputLabel>
                      <Select
                        value={formData.anxietyLevel}
                        onChange={(e) => handleInputChange('anxietyLevel', e.target.value)}
                        label="Anxiety Level (1-10)"
                      >
                        <MenuItem value="">Select Level</MenuItem>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(level => (
                          <MenuItem key={level} value={level}>{level} - {level <= 3 ? 'Mild' : level <= 6 ? 'Moderate' : level <= 8 ? 'Severe' : 'Very Severe'}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Depression Level (1-10)</InputLabel>
                      <Select
                        value={formData.depressionLevel}
                        onChange={(e) => handleInputChange('depressionLevel', e.target.value)}
                        label="Depression Level (1-10)"
                      >
                        <MenuItem value="">Select Level</MenuItem>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(level => (
                          <MenuItem key={level} value={level}>{level} - {level <= 3 ? 'Mild' : level <= 6 ? 'Moderate' : level <= 8 ? 'Severe' : 'Very Severe'}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Sleep & Appetite */}
            <Accordion 
              expanded={expandedSection === 'sleep-appetite'} 
              onChange={handleSectionChange('sleep-appetite')}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">
                  <Bedtime sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Sleep & Appetite
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Sleep Duration (hours)"
                      type="number"
                      value={formData.sleepDuration}
                      onChange={(e) => handleInputChange('sleepDuration', e.target.value)}
                      placeholder="e.g., 6, 7.5, 8..."
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Sleep Quality</InputLabel>
                      <Select
                        value={formData.sleepQuality}
                        onChange={(e) => handleInputChange('sleepQuality', e.target.value)}
                        label="Sleep Quality"
                      >
                        <MenuItem value="">Select Quality</MenuItem>
                        <MenuItem value="excellent">Excellent</MenuItem>
                        <MenuItem value="good">Good</MenuItem>
                        <MenuItem value="fair">Fair</MenuItem>
                        <MenuItem value="poor">Poor</MenuItem>
                        <MenuItem value="very_poor">Very Poor</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Sleep Issues (Select all that apply)
                    </Typography>
                    <FormGroup row>
                      {sleepIssuesOptions.map((issue) => (
                        <FormControlLabel
                          key={issue}
                          control={
                            <Checkbox
                              checked={formData.sleepIssues.includes(issue)}
                              onChange={(e) => handleArrayChange('sleepIssues', issue, e.target.checked ? 'add' : 'remove')}
                            />
                          }
                          label={issue}
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Appetite Changes</InputLabel>
                      <Select
                        value={formData.appetiteChanges}
                        onChange={(e) => handleInputChange('appetiteChanges', e.target.value)}
                        label="Appetite Changes"
                      >
                        <MenuItem value="">Select Change</MenuItem>
                        <MenuItem value="increased">Increased</MenuItem>
                        <MenuItem value="decreased">Decreased</MenuItem>
                        <MenuItem value="normal">Normal</MenuItem>
                        <MenuItem value="variable">Variable</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Weight Change (kg)"
                      type="number"
                      value={formData.weightChangeAmount}
                      onChange={(e) => handleInputChange('weightChangeAmount', e.target.value)}
                      placeholder="e.g., +5, -3, 0..."
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Risk Assessment */}
            <Accordion 
              expanded={expandedSection === 'risk-assessment'} 
              onChange={handleSectionChange('risk-assessment')}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" color="error">
                  <Warning sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Risk Assessment
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Important:</strong> Please answer these questions honestly. This information helps us provide the best care and ensure your safety.
                      </Typography>
                    </Alert>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl component="fieldset">
                      <Typography variant="subtitle2" gutterBottom>
                        Suicidal Thoughts
                      </Typography>
                      <RadioGroup
                        value={formData.suicidalThoughts}
                        onChange={(e) => handleInputChange('suicidalThoughts', e.target.value)}
                      >
                        <FormControlLabel value="no" control={<Radio />} label="No" />
                        <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="sometimes" control={<Radio />} label="Sometimes" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  
                  {formData.suicidalThoughts !== 'no' && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Suicidal Plan"
                          multiline
                          rows={2}
                          value={formData.suicidalPlan}
                          onChange={(e) => handleInputChange('suicidalPlan', e.target.value)}
                          error={!!errors.suicidalPlan}
                          helperText={errors.suicidalPlan}
                          placeholder="Describe any specific plans..."
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>Suicidal Intent</InputLabel>
                          <Select
                            value={formData.suicidalIntent}
                            onChange={(e) => handleInputChange('suicidalIntent', e.target.value)}
                            label="Suicidal Intent"
                          >
                            <MenuItem value="">Select Intent</MenuItem>
                            <MenuItem value="low">Low</MenuItem>
                            <MenuItem value="moderate">Moderate</MenuItem>
                            <MenuItem value="high">High</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl component="fieldset">
                      <Typography variant="subtitle2" gutterBottom>
                        Homicidal Thoughts
                      </Typography>
                      <RadioGroup
                        value={formData.homicidalThoughts}
                        onChange={(e) => handleInputChange('homicidalThoughts', e.target.value)}
                      >
                        <FormControlLabel value="no" control={<Radio />} label="No" />
                        <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="sometimes" control={<Radio />} label="Sometimes" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  
                  {formData.homicidalThoughts !== 'no' && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Homicidal Plan"
                          multiline
                          rows={2}
                          value={formData.homicidalPlan}
                          onChange={(e) => handleInputChange('homicidalPlan', e.target.value)}
                          error={!!errors.homicidalPlan}
                          helperText={errors.homicidalPlan}
                          placeholder="Describe any specific plans..."
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>Homicidal Intent</InputLabel>
                          <Select
                            value={formData.homicidalIntent}
                            onChange={(e) => handleInputChange('homicidalIntent', e.target.value)}
                            label="Homicidal Intent"
                          >
                            <MenuItem value="">Select Intent</MenuItem>
                            <MenuItem value="low">Low</MenuItem>
                            <MenuItem value="moderate">Moderate</MenuItem>
                            <MenuItem value="high">High</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Treatment Goals */}
            <Accordion 
              expanded={expandedSection === 'treatment-goals'} 
              onChange={handleSectionChange('treatment-goals')}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">
                  <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Treatment Goals & Expectations
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Treatment Goals (Select all that apply)
                    </Typography>
                    <FormGroup row>
                      {treatmentGoalsOptions.map((goal) => (
                        <FormControlLabel
                          key={goal}
                          control={
                            <Checkbox
                              checked={formData.treatmentGoals.includes(goal)}
                              onChange={(e) => handleArrayChange('treatmentGoals', goal, e.target.checked ? 'add' : 'remove')}
                            />
                          }
                          label={goal}
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Patient Expectations"
                      multiline
                      rows={3}
                      value={formData.patientExpectations}
                      onChange={(e) => handleInputChange('patientExpectations', e.target.value)}
                      placeholder="What do you hope to achieve from treatment? What are your expectations?"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleSkip}
              disabled={saving}
            >
              Skip for Now
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Intake Form'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PatientIntakeForm;
